<template>
  <div>
    <div id="overlay" v-if="fetchingData">
      <div class="spinner"></div>
    </div>

    <h1>{{form.projectType | capitalize}} Application</h1>

    <div v-if="form.projectType === 'demolition'">
      <div>
        <h7><i>Effective <b>Wednesday, October 15, 2025</b>, all asbestos building inspection reports submitted to DES must also include the following information:</i></h7>
      </div>

      <ul>
        <li>
          <h7><i><b>Sampling Methodology</b>: A detailed description of the methodology used to determine sampling locations, ensuring a representative collection of samples from all suspect homogeneous areas.</i></h7>
        </li>

        <li>
          <h7><i><b>Sample Area Identification and Scaled Drawing</b>: Clear identification of each sample area, accompanied by a drawing of the building/area to scale with dimensions.</i></h7>
        </li>

        <li>
          <h7><i><b>Precise Sample Locations</b>: The scaled drawing must clearly and precisely indicate each sample location.</i></h7>
        </li>

        <li>
          <h7><i><b>Contextual Photographs</b>: Clear photographs of the material from which each sample was taken. Photographs must provide sufficient context by showing adjacent homogeneous surfaces.</i></h7>
        </li>
      </ul>
    </div>


    <h7 v-if="form.projectType === 'asbestos'">
      Effective March 1, 2024, the asbestos abatement permit fee shall not be applicable to asbestos removal work prior to a demolition of a residential structure performed by or for any municipality. To apply for an asbestos abatement permit under this fee exemption, complete the permit application and click on “Save & Return Later” to obtain a temporary application number. Email the department at asbestosdemo@cookcountyil.gov and include:
      1) Temporary application number and 2) Letter from the municipality on letterhead stating the work will be performed by or for the municipality.
    </h7>

    <br>

    <p v-if="form.projectType === 'asbestos'">
      To complete the asbestos permit application with a variance request, you must provide documentation of emergency.
    </p>

    <p  v-if="form.projectType === 'demolition'">
      To complete the {{form.projectType}} permit application you will need the following documents:
    </p>

    <ol v-if="form.projectType === 'demolition'">
      <li>Plat of Survey/Arial View of Site</li>
      <li>Cook County Property Tax Summary</li>
      <li>Asbestos Inspection Report</li>
    </ol>

    <div class="form-wrapper">
      <div class="form-navbar">
        <div class="title-nav">
          <label class="col-form-label ml-2 d-none d-sm-block">
            {{form.projectType | capitalize}} Application
          </label>
          <div class="btn-group" role="group" aria-label="Save And Close Buttons">
            <button class="btn btn-link"
                    @click="saveApplication"
                    v-bind:disabled="!canSave">
              <font-awesome-icon :icon="['far', 'save']" />
              Save <span class="d-none d-lg-inline-block">&amp; Return Later</span>
            </button>
            <button class="btn btn-link" @click="cancel">
              <font-awesome-icon icon="times" />
              Cancel
            </button>
            <button class="btn btn-link"
                    v-if="isLastPage"
                    @click="submitForm">
              <font-awesome-icon :icon="['far', 'check-circle']" />
              Submit Form
            </button>
          </div>
        </div>

        <div class="nav-links">
          <a class="btn btn-link end" @click="previousSection">
            <font-awesome-icon icon="chevron-left" />
          </a>
          <router-link
            v-for="(route, index) in routes"
            :to="{path: route, query: $route.query}"
            v-bind:key="route"
            v-bind:class="{visited: index < currentRoute, error: $v.pages[route].$error}">
            {{index + 1}}
          </router-link>
          <a class="btn btn-link end" @click="nextSection">
            <font-awesome-icon icon="chevron-right" />
          </a>
        </div>
      </div>
      <div class="section-title">
        {{(currentRoute + 1) + '. ' + title}}
      </div>

      <div class="row no-gutters">
        <div class="col">
          <router-view :form="form"
                       :val="$v"
                       class="h-100 d-flex align-content-between flex-wrap"
                       v-bind:fetchingData.sync="fetchingData"
                       v-bind:title.sync="title">
          </router-view>
        </div>
        <div class="col-3 d-md-block d-none">
          <div class="save-panel">
            <div class="button-wrapper">
              <button class="btn btn-primary"
                      @click="saveApplication"
                      v-bind:disabled="!canSave">
                <font-awesome-icon :icon="['far', 'save']" />
                Save <span class="d-none d-lg-inline-block">&amp; Return Later</span>
              </button>
              <div class="w-100"></div>
              <button class="btn btn-primary mt-2" @click="cancel">
                <font-awesome-icon icon="times" />
                Cancel
              </button>
              <div class="w-100"></div>
              <button class="btn btn-success mt-2"
                      v-if="isLastPage"
                      @click="submitForm">
                <font-awesome-icon :icon="['far', 'check-circle']" />
                Submit Form
              </button>

              <dl class="row justify-content-end mt-2">
                <dt class="col" v-if="varianceFee > 0">Variance Fee:</dt>
                <dd class="col-auto" v-if="varianceFee > 0">${{varianceFee}}</dd>
                <div class="w-100"></div>
                <dt class="col" v-if="filingFee > 0">Filing Fee:</dt>
                <dd class="col-auto" v-if="filingFee > 0">${{filingFee}}</dd>
                <div class="w-100"></div>
                <dt class="col" v-if="inspectionFee > 0">Inspection Fee:</dt>
                <dd class="col-auto" v-if="inspectionFee > 0">${{inspectionFee}}</dd>
                <div class="w-100"></div>
                <dt class="col">
                  <strong>Total Price:</strong>
                </dt>
                <dd class="col-auto">
                  <strong>${{totalFee}}</strong>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { PermitApplication } from '~/js/FormObjects';
import { PermitApplicationValidation } from '@/js/FormValidations';
import { getApplication, getPrices, capitalize, errorNotification } from '~/js/FormFunctions';
import { CapitalizeFilter } from '@/js/Mixins';

const sumReducer = (accumulator, currentValue) => accumulator + currentValue;

export default {
  name: 'Application',
  props: {
    projectType: {
      validator(value) {
        return ['demolition', 'asbestos'].indexOf(value) !== -1;
      },
    },
  },
  mixins: [CapitalizeFilter],
  data() {
    return {
      form: new PermitApplication(this.projectType),
      title: null,
      currentRoute: 0,
      routes: [
        'contact',
        'site',
        'project',
        'materials',
        'asbestos',
        'signature',
      ],
      permitPrices: [],
      fetchingData: false,
    };
  },
  mounted() {
    if (this.projectType.toLowerCase() !== 'asbestos') {
      this.routes.splice(this.routes.indexOf('asbestos'), 1);
    }
    if (this.projectType.toLowerCase() !== 'demolition') {
      this.routes.splice(this.routes.indexOf('materials'), 1);
    }

    this.currentRoute = this.routes.findIndex(route => this.$route.path.endsWith(route));

    // so we can reference it within axios success functions
    const self = this;

    if (this.$store.getters.temporaryForm) {
      this.$set(this, 'form', this.$store.getters.temporaryForm);
      this.$store.commit('temporaryForm', null);
    }

    getPrices(this.projectType)
      .then((data) => {
        self.permitPrices = data;
      });

    if (this.$route.query.applicationNumber && !this.form.id) {
      this.fetchingData = true;
      getApplication(this.$route.query.applicationNumber)
        .then((data) => {
          const form = data;
          if (!form.projectType) {
            form.projectType = capitalize(self.projectType);
          }
          self.$set(self, 'form', form);
          self.fetchingData = false;
        })
        .catch(() => {
          self.$router.push({ name: 'PermitSearch', params: { projectType: self.projectType } });
          self.fetchingData = false;
        });
    }
  },
  validations: {
    form: new PermitApplicationValidation(),
    pages: {
      contact: ['form.contactEmail', 'form.confirmEmail'],
      site: ['form.facility', 'form.files.buildingConstructionDate'],
      project: ['form.contractor', 'form.contractorContactFirstName', 'form.contractorContactLastName',
        'form.contractorContactPhone', 'form.contractorContactEmail', 'form.varianceNotesPublicHealth',
        'form.varianceNoRefundAcknowledgement', 'form.startDate', 'form.endDate', 'form.workHours', 'form.disposalSite',
        'form.transporter', 'form.files.varianceHealthRisk', 'form.specialCircumstances', 'form.partialFullDemo'],
      materials: ['form.materials'],
      asbestos: ['form.asbestosData'],
      signature: ['form.name', 'form.title', 'form.signDate', 'form.files.platOfSurvey', 'form.files.taxSummary'],
    },
  },
  methods: {
    previousSection() {
      if (this.currentRoute > 0) {
        this.$router.push({ path: this.routes[this.currentRoute - 1], query: this.$route.query });
      }
    },
    nextSection() {
      if (this.currentRoute < this.routes.length) {
        this.$router.push({ path: this.routes[this.currentRoute + 1], query: this.$route.query });
      }
    },
    submitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$notify('Errors during permit application validation.', 'error');
        return;
      }

      const vm = this;
      this.mySaveApplication()
        .then((response) => {
          vm.form.id = response.data;

          Object.keys(this.form.files).forEach((key) => {
            if (vm.form.files[key]) {
              vm.form.existingFileDetails[key] = {
                documentName: key,
                name: vm.form.files[key].name,
              };
            }
          });
          vm.$store.commit('application', vm.form);
          vm.$router.push({ name: 'PermitApplicationReview', params: { projectType: vm.projectType } });
        })
        .catch(() => {});
    },
    saveApplication() {
      const self = this;
      this.mySaveApplication()
        .then((response) => {
          self.$router.push({
            name: 'PermitApplicationSaved',
            params: {
              projectType: self.projectType.toLowerCase(),
            },
            query: {
              applicationId: response.data,
            },
          });
        })
        .catch(() => {});
    },
    mySaveApplication() {
      // update the stored list of contractors if the name changed
      if (this.form.projectType.toLowerCase() === 'demolition') {
        if (this.form.contractor && this.form.contractor.id) {
          const contractors = this.$store.getters.contractors(this.form.projectType.toLowerCase());
          const contractor = contractors.find(c => c.id === this.form.contractor.id);
          if (contractor && contractor.name !== this.form.contractor.name) {
            contractor.name = this.form.contractor.name;
          }
        } else if (this.form.contractor.name) {
          /**
           * we can't add the contractor to the list if its a new contractor since we won't know the id. we'll just delete
           * the list and re-fetch it again.
           */
          this.$store.commit('demolitionContractors', []);
        }
      }

      const self = this;

      const formData = new FormData();
      Object.keys(this.form.files).forEach((key) => {
        if (self.form.files[key]) {
          formData.append(key, self.form.files[key]);
        }
      });

      formData.append('form', JSON.stringify(this.form));
      this.fetchingData = true;
      return new Promise((resolve, reject) => {
        self.$http.post('/api/permit/save', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
          .then((response) => {
            // console.log(response)
            self.fetchingData = false;
            resolve(response);
          })
          .catch((error) => {
            self.fetchingData = false;
            errorNotification('Error saving application record: ', error);
            reject(error);
          });
      });
    },
    cancel() {
      this.$router.push({ name: 'PermitApplicationCancel', params: { projectType: this.projectType } });
    },
  },
  watch: {
    $route(to) {
      this.currentRoute = this.routes.findIndex(route => to.path.endsWith(route));
    },
  },
  computed: {
    isLastPage() {
      return this.currentRoute === this.routes.length - 1;
    },
    canSave() {
      return !this.$v.form.confirmEmail.$invalid;
    },
    facilityType() {
      return this.form.facility.type && this.form.facility.type.startsWith('Residential') ? 'residential' : 'commercial';
    },
    buildingCount() {
      if (this.facilityType === 'residential') {
        let houseCount = parseInt(this.form.facility.houseCount, 10);
        let garageCount = parseInt(this.form.facility.garageCount, 10);
        let shedCount = parseInt(this.form.facility.shedCount, 10);
        houseCount = Number.isNaN(houseCount) ? 0 : houseCount;
        garageCount = Number.isNaN(garageCount) ? 0 : garageCount;
        shedCount = Number.isNaN(shedCount) ? 0 : shedCount;
        return houseCount + garageCount + shedCount;
      }
      return 1;
    },
    varianceFee() {
      let price = 0.00;
      if (!this.permitPrices || this.permitPrices.length === 0) {
        return price;
      }

      if (this.form.varianceCheckCourtOrder ||
        this.form.varianceCheckFinancialBurden ||
        this.form.varianceCheckPublicHealth) {
        price += this.permitPrices
          .filter(f => f.feeType === 'variance')
          .filter(f => f.division === this.form.projectType.toLowerCase())
          .map(f => f.price)
          .reduce(sumReducer);
      }
      return price;
    },
    filingFee() {
      let price = 0.00;
      if (!this.permitPrices || this.permitPrices.length === 0 || !this.form.facility.type) {
        return price;
      }

      const division = this.projectType.toLowerCase() === 'asbestos' ? 'asbestos' : 'demolition';
      const permitType = this.facilityType;

      price += this.permitPrices
        .filter(f => f.division === division)
        .filter(f => f.feeType === 'filing')
        .filter(f => f.permitType === permitType)
        .map(f => f.price)[0];
      return price;
    },
    inspectionFee() {
      let price = 0.00;
      if (!this.permitPrices || this.permitPrices.length === 0 || !this.form.facility.type) {
        return price;
      }
      const permitType = this.facilityType;
      if (this.projectType.toLowerCase() === 'asbestos') {
        const asbestosPrices = this.permitPrices.filter(f => f.division === 'asbestos');
        if (permitType === 'commercial') {
          const linearFtPrice = asbestosPrices
            .filter(f => f.feeType === 'inspection')
            .filter(f => f.unitType === 'linear_feet')
            .map(f => f.price)[0];

          const squareFtPrice = asbestosPrices
            .filter(f => f.feeType === 'inspection')
            .filter(f => f.unitType === 'square_feet')
            .map(f => f.price)[0];

          let inspectionFee = (this.form.asbestosData.linearftTotalRemoval * linearFtPrice);
          inspectionFee += (this.form.asbestosData.surfaceAreaTotalRemoval * squareFtPrice);

          if (inspectionFee > 2000) {
            inspectionFee = 2000;
          }
          price += inspectionFee;
        }
      } else {
        const buildingPrice = this.permitPrices
          .filter(f => f.division === 'demolition')
          .filter(f => f.permitType === permitType)
          .filter(f => f.feeType === 'inspection')
          .map(f => f.price)[0];
        price += (buildingPrice * this.buildingCount);
      }
      return price;
    },
    totalFee() {
      return this.varianceFee +
        this.filingFee +
        this.inspectionFee;
    },
  },
};
</script>
